/**
 * @fileoverview Componente para selección de variantes de producto
 * @module ProductVariantsComponent
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.0.0
 */
import React, { useEffect, useState, memo } from 'react';
import {
  isEmpty, filter, head, forEach
} from 'lodash';

/**
 * @typedef {Object} Product
 * @description Objeto producto con variantes
 * @property {Array} product_variants - Lista de variantes del producto
 * @property {Array} product_variant_combinations - Combinaciones disponibles
 * @property {string} currency - Código de moneda
 * @property {string} description - Descripción del producto
 */

/**
 * Componente Molecule para selección de variantes de producto.
 *
 * Implementa el patrón **Molecule** de Atomic Design para manejar
 * la selección de variantes y combinaciones de productos.
 *
 * @component ProductVariantsComponent
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Selección dinámica de variantes
 * - Validación de combinaciones disponibles
 * - Estados visuales para opciones seleccionadas/disponibles
 * - Actualización automática del producto seleccionado
 * - Estilos Tailwind CSS migrados del original
 * - Optimizado con React.memo
 *
 * @param {Object} props - Propiedades del componente
 * @param {Product} props.product - Producto con variantes
 * @param {function} props.setSelectedProductToSend - Función para actualizar producto seleccionado
 *
 * @returns {JSX.Element} Elemento JSX con selector de variantes
 *
 * @example
 * ```jsx
 * <ProductVariantsComponent
 *   product={productData}
 *   setSelectedProductToSend={setSelectedProduct}
 * />
 * ```
 */
const ProductVariantsComponent = memo(({
  product,
  setSelectedProductToSend
}) => {
  const { product_variants, product_variant_combinations } = product;

  const [variants, setVariants] = useState([]);

  useEffect(() => {
    if (isEmpty(product_variant_combinations)) {
      return;
    }

    const newVariants = selectInitialCombination();
    setVariants(enabledCombinations(newVariants));
  }, [product_variants, product_variant_combinations]);

  const selectInitialCombination = () => {
    let firstCombIds = [];

    if (!isEmpty(product_variant_combinations)) {
      firstCombIds = head(product_variant_combinations).variant_option_web_ids;
      setSelectedProductToSend(addExtraFields(head(product_variant_combinations)));
    }

    return product_variants.map((variant, variantIndex) => {
      const options = variant.product_variant_options.map((opt) => {
        const isSelected = firstCombIds.includes(opt.web_id);
        return {
          ...opt, selected: isSelected, available: true, variant_index: variantIndex
        };
      });
      return { ...variant, product_variant_options: options, index: variantIndex };
    });
  };

  const addExtraFields = (item) => {
    let imageUrls = [];

    if (!isEmpty(item.attached_image)) {
      imageUrls = [{ ...item.attached_image }];
    } else if (!isEmpty(item.product_image)) {
      imageUrls = [{ ...item.product_image }];
    }

    return {
      ...item,
      title: `${item.product_title} - ${item.name}`,
      currency: product.currency,
      description: product.description,
      image_urls: imageUrls,
      is_combination: true,
      discounted_price: item.discounted_price || product.discounted_price
    };
  };

  const getSelectedCombinationsUntil = (newVariants, until) => {
    if (until < 0) return [];

    const webIds = [];

    forEach(newVariants, (variant) => {
      forEach(variant.product_variant_options, (option) => {
        if (variant.index <= until && option.selected) {
          webIds.push(option.web_id);
        }
      });
    });

    return webIds;
  };

  const enabledCombinations = (newVariants) => newVariants.map((variant) => {
    const options = variant.product_variant_options.map((opt) => ({
      ...opt,
      available: existCombination([
        ...getSelectedCombinationsUntil(newVariants, variant.index - 1),
        opt.web_id
      ])
    }));

    return { ...variant, product_variant_options: options };
  });

  const existCombination = (selectedWebIds) => !isEmpty(
    filter(product_variant_combinations, (o) => selectedWebIds.every((element) => o.variant_option_web_ids.includes(element)))
  );

  const handleSelect = (option) => {
    const newVariants = variants.map((variant) => {
      const options = variant.product_variant_options.map((opt) => {
        if (variant.index === option.variant_index) {
          if (option.web_id === opt.web_id) {
            return { ...opt, selected: true };
          }
          return { ...opt, selected: false };
        }
        return opt;
      });

      return { ...variant, product_variant_options: options };
    });

    const selectedCombinations = getSelectedCombinationsUntil(newVariants, newVariants.length);
    const combinations = filter(product_variant_combinations, (o) => selectedCombinations.every((element) => o.variant_option_web_ids.includes(element)));

    if (combinations[0]) {
      setSelectedProductToSend(addExtraFields(combinations[0]));
    } else {
      setSelectedProductToSend({});
    }

    setVariants(enabledCombinations(newVariants));
  };

  return (
    <div className="tw-w-full tw-px-0">
      {variants.map((variant) => (
        <div key={`variant-web-${variant.id}`} className="tw-flex tw-flex-col">
          <div className="tw-py-2">
            <span className="tw-font-semibold tw-text-gray-950 tw-text-sm">
              {variant.name}
            </span>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {variant.product_variant_options.map((opt) => (
              <button
                type="button"
                key={`variant-opt-${opt.web_id}`}
                className={`
                  tw-font-normal tw-text-xs tw-py-2 tw-px-3 tw-rounded-lg 
                  tw-border tw-min-w-11 tw-mr-2.5
                  ${opt.selected && opt.available
                  ? 'tw-bg-gray-800 tw-text-white tw-border-gray-800'
                  : 'tw-bg-white tw-text-gray-800 tw-border-gray-800'
                  }
                  ${!opt.available
                    ? 'tw-line-through tw-border-gray-400 tw-text-gray-400 tw-opacity-50'
                    : 'hover:tw-bg-gray-100'
                  }
                `}
                onClick={() => handleSelect(opt)}
                disabled={!opt.available}
              >
                <span className="tw-text-xs">{opt.name}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
ProductVariantsComponent.displayName = 'ProductVariantsComponent';

export default ProductVariantsComponent;
